/** Time-zone helpers on plain Intl (no date library): wall-clock ↔ instant in an IANA zone, and <input type="datetime-local"> values. */

export const COMMON_TIMEZONES = [
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "America/Toronto",
  "Europe/London", "Europe/Paris", "Europe/Istanbul", "Asia/Dubai", "Asia/Karachi", "Asia/Kolkata", "Asia/Tehran",
  "Asia/Baghdad", "Asia/Riyadh", "Asia/Jakarta", "Australia/Sydney", "UTC",
];

export function browserTimeZone(): string {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; } catch { return "UTC"; }
}

/** The zones to offer in a select: the common list, plus the browser's and the current value if missing. */
export function timeZoneOptions(current?: string): string[] {
  const set = new Set(COMMON_TIMEZONES);
  set.add(browserTimeZone());
  if (current) set.add(current);
  return [...set].sort((a, b) => (a === "UTC" ? 1 : b === "UTC" ? -1 : a.localeCompare(b)));
}

export function isValidTimeZone(tz: string): boolean {
  try { new Intl.DateTimeFormat("en-US", { timeZone: tz }); return true; } catch { return false; }
}

type Parts = { y: number; m: number; d: number; h: number; mi: number };

/** The wall-clock fields of `date` in `tz`. */
export function partsInZone(date: Date, tz: string): Parts {
  const f = new Intl.DateTimeFormat("en-US", { timeZone: tz, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  const get = (t: string) => Number(f.formatToParts(date).find((p) => p.type === t)?.value ?? 0);
  return { y: get("year"), m: get("month"), d: get("day"), h: get("hour") % 24, mi: get("minute") };
}

function offsetMs(date: Date, tz: string): number {
  const p = partsInZone(date, tz);
  const asUtc = Date.UTC(p.y, p.m - 1, p.d, p.h, p.mi);
  return asUtc - Math.floor(date.getTime() / 60_000) * 60_000;
}

/** The instant at which the wall clock in `tz` reads y-m-d hh:mm (DST gaps resolve forward, overlaps to the first). */
export function zonedInstant(y: number, m: number, d: number, hhmm: string, tz: string): Date {
  const [h, mi] = hhmm.split(":").map(Number) as [number, number];
  const guess = Date.UTC(y, m - 1, d, h, mi);
  let t = guess - offsetMs(new Date(guess), tz);
  const second = guess - offsetMs(new Date(t), tz);
  if (second !== t) t = Math.min(t, second);
  return new Date(t);
}

/** Calendar day `offset` days after today in `tz`, as y/m/d. */
export function dayInZone(from: Date, offset: number, tz: string): { y: number; m: number; d: number } {
  const p = partsInZone(from, tz);
  const u = new Date(Date.UTC(p.y, p.m - 1, p.d + offset));
  return { y: u.getUTCFullYear(), m: u.getUTCMonth() + 1, d: u.getUTCDate() };
}

/** "09:00" as the owner's local clock time, e.g. "9:00 AM". */
export function timeOnly(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

/** ISO → the value an <input type="datetime-local"> expects, in the browser's zone. */
export function toLocalInput(iso: string | null | undefined, fallbackMs = Date.now() + 3600_000): string {
  const d = iso ? new Date(iso) : new Date(fallbackMs);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** A datetime-local value (browser zone) → ISO UTC, or null when empty/invalid. */
export function fromLocalInput(value: string): string | null {
  if (!value) return null;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? null : new Date(t).toISOString();
}
